import classNames from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { Col, Row } from "react-grid-system";
import { getDisplayError } from "../../lib/api/endpoints";
import { GetAccountResponse } from "../../lib/api/models";
import { useGetPaymentHistory } from "../../lib/api/queries";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import Container from "../common/Container";
import Spinner from "../common/Spinner";
import ToolLoadError from "../common/ToolLoadError";
import StyledButton from "../common/buttons/StyledButton";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./PaymentHistory.module.css";
import PaymentHistoryTable from "./PaymentHistoryTable";

const strings = { close: "Close" };

type PaymentHistoryProps = {
  account: GetAccountResponse;
  className?: string;
  toolId: string;
  onRequestClose: () => void;
};

export default function PaymentHistory({
  className,
  account,
  toolId,
  onRequestClose,
}: PaymentHistoryProps) {
  const {
    isLoading,
    isError,
    error: getPaymentHistoryError,
    data: getPaymentHistoryData,
    refetch: refetchPaymentHistory,
  } = useGetPaymentHistory(account.currentDwbuid);
  const headerRef = useRef<HTMLHeadingElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (headerRef?.current?.clientHeight) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, []);

  return (
    <div
      className={classNames(
        toolStyles.container,
        styles.mainContainer,
        className,
      )}
      id={toolId}
    >
      <Container>
        <div ref={headerRef}>
          <Row className={toolStyles.header}>
            <Col md={11} className={toolStyles.centerCol}>
              <h2 className={toolStyles.title}>
                {toolTypeToName(ToolType.PaymentHistory)}
              </h2>
            </Col>
            <Col md={1} className={toolStyles.centerCol}>
              <StyledButton
                className={toolStyles.headerButton}
                variant="secondary"
                renderLoadingIndicator={false}
                onClick={onRequestClose}
              >
                {strings.close}
              </StyledButton>
            </Col>
          </Row>
        </div>
        {isLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : isError ? (
          <Row>
            <Col>
              <ToolLoadError
                toolName={toolTypeToName(ToolType.PaymentHistory)}
                errorDescription={
                  getDisplayError(getPaymentHistoryError)?.message
                }
                onRetry={refetchPaymentHistory}
              />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>
              <div
                className={styles.tableContainer}
                style={{
                  maxHeight: `calc(100dvh - ${headerHeight}px - 72px)`,
                }}
              >
                <PaymentHistoryTable history={getPaymentHistoryData} />
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
